import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { EligibleGamesDialog } from "@/components/campaigns/campaign-shell/eligible-games-dialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * "N games ›" — the ONE way the widget offers a list of games to browse. There is no thumbnail
 * art yet, so no tiles stand in for it: a quiet ghost pill in the text colour (the same chip as
 * the "+N" counts and the place badges) that opens the games dialog. Pass `games` when the
 * client has the names (a picked list, a prize's games) or `campaignId` to page them from the
 * server (an "All games" selection); `label` replaces the count copy ("All games"). `title`
 * names the dialog.
 */
export const GamesPill = ({
  count,
  games,
  campaignId,
  title,
  label,
  className,
}: {
  /** The real total, for the pill's copy and the dialog's count badge. */
  count?: number;
  games?: string[];
  campaignId?: number;
  title?: string;
  label?: string;
  className?: string;
}) => {
  const { t } = useTranslation();
  const text =
    label ??
    (count != null
      ? t("eligibleGames.count", "{{count}} games", {
          count,
          // Shown grouped ("40,213"); `count` stays a number for plural resolution.
          replace: { count: count.toLocaleString() },
        })
      : t("eligibleGames.allGames", "All games"));
  return (
    <EligibleGamesDialog
      games={games}
      campaignId={campaignId}
      count={count}
      title={title}
      trigger={
        <button
          type="button"
          className={cn(
            // `focus-ring`: the plain-CSS keyboard ring (index.css); Tailwind's ring / outline
            // utilities don't render in the shadow root.
            "focus-ring inline-flex shrink-0 cursor-pointer rounded-full transition-opacity outline-none hover:opacity-80",
            className
          )}
        >
          <Badge variant="ghost" color="foreground" className="font-semibold">
            <span className="inline-flex items-center gap-0.5">
              {text}
              <ChevronRight
                aria-hidden
                className="-mr-0.5 size-3.5"
                strokeWidth={2.5}
              />
            </span>
          </Badge>
        </button>
      }
    />
  );
};
